import { useEffect, useId, useState } from 'react';
import { createProblemSpec, updateProblemSpec } from '../services/api';
import './SpecFormModal.css';

const emptyForm = {
  nameTh: '',
  nameEn: '',
  description: '',
  maxSize: 0,
  maxCount: 0,
  severity: 'medium',
};

export default function SpecFormModal({
  open,
  mode = 'create',
  initial = null,
  onClose,
  onSaved,
}) {
  const titleId = useId();
  const isEdit = mode === 'edit';
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    if (isEdit && initial) {
      setForm({
        nameTh: initial.nameTh || '',
        nameEn: initial.nameEn || '',
        description: initial.description || '',
        maxSize: initial.maxSize ?? 0,
        maxCount: initial.maxCount ?? 0,
        severity: initial.severity || 'medium',
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, isEdit, initial]);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === 'Escape' && !saving) onClose?.();
    }
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, saving, onClose]);

  if (!open) return null;

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.nameTh.trim()) {
      setError('กรุณาระบุชื่อปัญหา (ภาษาไทย)');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        unit: 'mm',
        maxSize: Number(form.maxSize) || 0,
        maxCount: Number(form.maxCount) || 0,
        images: Array.isArray(initial?.images) ? initial.images : [],
      };
      if (isEdit && initial?.id) {
        await updateProblemSpec(initial.id, payload, []);
      } else {
        await createProblemSpec(payload, []);
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err.message || 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="spec-modal-root" role="presentation">
      <button
        type="button"
        className="spec-modal-backdrop"
        aria-label="ปิด"
        disabled={saving}
        onClick={() => !saving && onClose?.()}
      />
      <div
        className="spec-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="spec-modal-head">
          <div>
            <p className="spec-modal-kicker">กฎการตรวจ</p>
            <h2 id={titleId}>{isEdit ? 'แก้ไขปัญหา' : 'เพิ่มปัญหาใหม่'}</h2>
          </div>
          <button
            type="button"
            className="spec-modal-close"
            aria-label="ปิด"
            disabled={saving}
            onClick={() => !saving && onClose?.()}
          >
            ×
          </button>
        </header>

        <form className="spec-modal-form" onSubmit={handleSubmit}>
          <div className="spec-modal-body">
            <div className="grid-2">
              <div className="field">
                <label htmlFor="modal-nameTh">
                  ชื่อปัญหา (ภาษาไทย) <span style={{ color: 'var(--ng)' }}>*</span>
                </label>
                <input
                  id="modal-nameTh"
                  value={form.nameTh}
                  onChange={(e) => setField('nameTh', e.target.value)}
                  placeholder="เช่น จุดดำ"
                  required
                  autoFocus
                />
              </div>
              <div className="field">
                <label htmlFor="modal-nameEn">ชื่อปัญหา (English)</label>
                <input
                  id="modal-nameEn"
                  value={form.nameEn}
                  onChange={(e) => setField('nameEn', e.target.value)}
                  placeholder="e.g. Black Dot"
                />
              </div>
            </div>

            <div className="field">
              <label htmlFor="modal-description">รายละเอียดเงื่อนไขปัญหา</label>
              <textarea
                id="modal-description"
                className="spec-textarea"
                rows={4}
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
                placeholder="ระบุเงื่อนไข เช่น ลักษณะปัญหา สาเหตุ วิธีสังเกต และขนาดที่ยอมรับได้..."
              />
            </div>

            <div className="spec-form-metrics spec-form-metrics-3">
              <div className="field">
                <label htmlFor="modal-maxSize">ขนาดสูงสุด (mm)</label>
                <input
                  id="modal-maxSize"
                  type="number"
                  min="0"
                  step="0.1"
                  value={form.maxSize}
                  onChange={(e) => setField('maxSize', e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="modal-maxCount">จำนวนสูงสุด</label>
                <input
                  id="modal-maxCount"
                  type="number"
                  min="0"
                  step="1"
                  value={form.maxCount}
                  onChange={(e) => setField('maxCount', e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="modal-severity">ระดับความรุนแรง</label>
                <select
                  id="modal-severity"
                  value={form.severity}
                  onChange={(e) => setField('severity', e.target.value)}
                >
                  <option value="high">สูง (High)</option>
                  <option value="medium">กลาง (Medium)</option>
                  <option value="low">ต่ำ (Low)</option>
                </select>
              </div>
            </div>

            {error ? <p className="spec-modal-error">{error}</p> : null}
          </div>

          <footer className="spec-modal-foot">
            <button
              type="button"
              className="btn"
              disabled={saving}
              onClick={() => !saving && onClose?.()}
            >
              ยกเลิก
            </button>
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'กำลังบันทึก...' : 'บันทึกปัญหา'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
